import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatInputAttachment, Attachment, MessageAction, SiAiWelcomeScreenComponent, SiUserMessageComponent, SiChatInputComponent, SiChatInputDisclaimerDirective } from '@siemens/element-ng/chat-messages';
import { elementAttachment, elementSettings } from '@siemens/element-icons';
import { addIcons } from '@siemens/element-ng/icon';
import { SiTooltipDirective } from '@siemens/element-ng/tooltip';
import { SiInlineNotificationComponent } from '@siemens/element-ng/inline-notification';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { IMessage, IStreamingMessageV2 } from 'src/app/interfaces/streaming-messages.interface';
import { ContentWithText } from 'src/app/interfaces/content.interface';
import { MONACO_EDITOR_OPTIONS_READONLY } from 'src/app/constants/editor-options';
import { parseMarkdown } from 'src/app/utils/markdown.utils';
import { ALLOWED_FILE_EXTENSIONS_LOWER } from 'src/app/constants/file.constants';
import { MonacoCodeEditorComponent } from '../../editor/monaco-code-editor/monaco-code-editor.component';
import { UserSettingsService } from 'src/app/services/user-settings.service';
import { UserSettingsTooltip } from 'src/app/constants/user-settings-tooltips.constants';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { TokenUsageService } from 'src/app/services/token-usage.service';
@Component({
  selector: 'app-chat-copilot',
  imports: [
    CommonModule,
    FormsModule,
    SiUserMessageComponent,
    SiChatInputComponent,
    SiChatInputDisclaimerDirective,
    SiAiWelcomeScreenComponent,
    SiMenuModule,
    CdkMenuTrigger,
    SiInlineNotificationComponent,
    SiTooltipDirective,
    MonacoCodeEditorComponent
  ],
  templateUrl: './chat-copilot.component.html',
  styleUrl: './chat-copilot.component.scss',
})
export class ChatCopilotComponent {
  @Input() executeCommandList: IMessage[] = [];
  @Input() inputMessageText: string = '';
  @Input() isStreaming: boolean = false;
  @Input() chatAttachments: ChatInputAttachment[] = [];
  @Input() isElectronMode: boolean = false;
  @Input() psseVersions: string[] = [];
  @Input() selectedPsseVersion: string = '';
  @Input() isPsseDropdownDisabled: boolean = false;

  readonly userSettingsService = inject(UserSettingsService);
  readonly tokenUsageService = inject(TokenUsageService);
  private readonly toastService = inject(SiToastNotificationService);
  readonly UserSettingsTooltip = UserSettingsTooltip;

  readonly tokenUsage = this.tokenUsageService.tokenUsage;
  readonly tokenUsageFailed = this.tokenUsageService.tokenUsageFailed;
  readonly editorOptions = MONACO_EDITOR_OPTIONS_READONLY;
  readonly acceptedFileTypes = ALLOWED_FILE_EXTENSIONS_LOWER.map(ext => `.${ext}`).join(',');
  private readonly icons = addIcons({ elementAttachment, elementSettings });
  @ViewChild('chatInput') siChatInput?: SiChatInputComponent;
  @ViewChild('chatInput', { read: ElementRef }) chatInputRef?: ElementRef;
  readonly sending = signal(false);
  @Output() messageTextChange = new EventEmitter<string>();
  @Output() messageSend = new EventEmitter<void>();
  @Output() codeChange = new EventEmitter<string>();
  @Output() feedbackSend = new EventEmitter<{ cmd: IMessage, value: 'up' | 'down' }>();
  @Output() attachFilesClick = new EventEmitter<void>();
  @Output() removeFileClick = new EventEmitter<ChatInputAttachment | Attachment>();
  @Output() psseVersionChange = new EventEmitter<string>();
  @Output() stopWorkflow = new EventEmitter<void>();

  constructor() {
    effect(() => this.onTokenUsageFailed());
  }

  get chatInputActions(): MessageAction[] {
    return [
      {
        icon: this.icons.elementAttachment,
        label: 'Attach files',
        action: () => this.onAttachFiles(),
        disabled: this.isStreaming || this.tokenUsageFailed()
      }
    ];
  }

  onInputChange(value: string): void {
    this.messageTextChange.emit(value);
  }

  onSendMessage(_event?: { content: string; attachments: ChatInputAttachment[] }): void {
    this.sending.set(true);
    this.messageSend.emit();
    setTimeout(() => {
      const textarea = this.chatInputRef?.nativeElement?.querySelector('textarea');
      if (textarea) textarea.dispatchEvent(new Event('input'));
      if (this.siChatInput && this.chatAttachments.length > 0) {
        this.siChatInput.attachments.set([...this.chatAttachments]);
      }
      this.sending.set(false);
    }, 0);
  }

  onCodeChange(newCode: string): void {
    this.codeChange.emit(newCode);
  }

  onSendFeedback(cmd: IMessage, value: 'up' | 'down'): void {
    this.feedbackSend.emit({ cmd, value });
  }
  onAttachFiles(): void {
    this.attachFilesClick.emit();
  }

  onAttachmentsChange(newAttachments: (ChatInputAttachment | Attachment)[]): void {
    if (this.sending() || this.isStreaming) {
      // Restore the component's internal ModelSignal so the deleted item reappears
      this.siChatInput?.attachments.set([...this.chatAttachments]);
      return;
    }
    const removed = this.chatAttachments.find(
      a => !newAttachments.some(n => n.name === a.name)
    );
    if (removed) {
      this.removeFileClick.emit(removed);
    }
  }

  onPsseVersionChange(version: string): void {
    this.psseVersionChange.emit(version);
  }

  onStopWorkflow(): void {
    this.stopWorkflow.emit();
  }

  private onTokenUsageFailed(): void {
    if (this.tokenUsageFailed()) {
      this.toastService.showToastNotification({
        state: 'danger',
        title: 'Token Usage Unavailable',
        message: 'Failed to retrieve token usage details.'
      });
    }
  }

  getUsagePercent(total: number, max: number): string {
    if (max <= 0) return '0.0';
    const pct = (total / max) * 100;
    if (pct === 0) return '0.0';
    if (pct < 0.01) return pct.toFixed(4);
    if (pct < 0.1) return pct.toFixed(3);
    if (pct < 1) return pct.toFixed(2);
    return pct.toFixed(1);
  }
  
  onToggleInsights(): void {
    this.userSettingsService.update({ insights_enabled: !this.userSettingsService.settings().insights_enabled });
  }

  onToggleAutoCode(): void {
    this.userSettingsService.update({ auto_code_correction: !this.userSettingsService.settings().auto_code_correction });
  }

  isStatusMessage(message: IStreamingMessageV2): boolean {
    return message.kind === 'status' && !!message.message;
  }

  shouldShowTextContent(message: IStreamingMessageV2): boolean {
    return message.kind === 'content' &&
      message.content_type === 'text' &&
      !!message.content &&
      typeof message.content === 'string';
  }

  shouldShowCodeEditor(message: IStreamingMessageV2): boolean {
    return message.kind === 'content' &&
      message.content_type === 'code' &&
      !!message.content &&
      typeof message.content === 'string';
  }

  shouldShowCodeAsObject(message: IStreamingMessageV2): boolean {
    return message.kind === 'content' &&
      message.content_type === 'code' &&
      !!message.content &&
      typeof message.content === 'object';
  }

  getTextFromCodeObject(message: IStreamingMessageV2): string | Promise<string> {
    if (typeof message.content === 'object' && message.content !== null) {
      const text = (message.content as ContentWithText).text || '';
      return text ? parseMarkdown(text) : '';
    }
    return '';
  }

  getContentDisplay(message: IStreamingMessageV2): string | Promise<string> {
    if (message.content_type === 'text' && typeof message.content === 'string') {
      return parseMarkdown(message.content);
    }
    if (typeof message.content === 'string') {
      return message.content;
    }
    return '';
  }

  calculateHeight(content: string | undefined): number {
    if (!content) return 100;
    const lineCount = content.split('\n').length;
    const lineHeight = 20; // approximate height of each line in pixels
    return Math.max(100, lineCount * lineHeight);
  }

}


